import { serializeTenantQueryArgs } from '@core/api/tenantContext';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api/baseQuery';

/**
 * Projects API using RTK Query
 */
export const projectsApi = createApi({
  serializeQueryArgs: serializeTenantQueryArgs,
  reducerPath: 'projectsApi',
  baseQuery,
  tagTypes: ['Project'],
  endpoints: (builder) => ({
    getProjects: builder.query({
      query: () => '/api/projects/',
      // Tolerates both shapes so the list never silently truncates: this
      // endpoint is unpaginated, but a paginated response must not read as
      // "no projects" if that ever changes back.
      transformResponse: (response) => (Array.isArray(response) ? response : (response?.results ?? [])),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'Project', id: uuid })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
    }),

    getProject: builder.query({
      query: (uuid) => `/api/projects/${uuid}/`,
      providesTags: (result, error, uuid) => [{ type: 'Project', id: uuid }],
    }),

    createProject: builder.mutation({
      query: (data) => ({
        url: '/api/projects/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    updateProject: builder.mutation({
      query: ({ uuid, ...data }) => ({
        url: `/api/projects/${uuid}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { uuid }) => [
        { type: 'Project', id: uuid },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    deleteProject: builder.mutation({
      query: (uuid) => ({
        url: `/api/projects/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, uuid) => [
        { type: 'Project', id: uuid },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    archiveProject: builder.mutation({
      query: (uuid) => ({
        url: `/api/projects/${uuid}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, uuid) => [
        { type: 'Project', id: uuid },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    restoreProject: builder.mutation({
      query: (uuid) => ({
        url: `/api/projects/${uuid}/restore/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, uuid) => [
        { type: 'Project', id: uuid },
        { type: 'Project', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useArchiveProjectMutation,
  useRestoreProjectMutation,
} = projectsApi;
